import { useMemo } from "react";
import { Box, Typography, Skeleton, Tooltip } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { Calendar, CalendarClock, List } from "lucide-react";

import { Card } from "../../components/Card";
import { Button } from "../../components/Button";
import { Avatar } from "../../components/Avatar";
import { getData } from "../../components/hooks/getData";
import { useAuth } from "../../components/contexts/AuthContext";
import { useTheme as useCustomTheme } from "../../theme/useTheme";
import { PLATFORM_META } from "./workflow/platformMeta";

const MAX_ITEMS = 10;
// Several adaptations per piece: fetch enough to fill MAX_ITEMS pieces.
const ADAPTATIONS_LIMIT = 60;

const startOfDay = (date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());

const formatDayLabel = (date) => {
  const diffDays = Math.round((startOfDay(date) - startOfDay(new Date())) / 86400000);
  if (diffDays === 0) return "Hoy";
  if (diffDays === 1) return "Mañana";
  const label = date.toLocaleDateString("es-ES", { weekday: "short", day: "numeric", month: "short" });
  return label.charAt(0).toUpperCase() + label.slice(1);
};

const formatTime = (date) =>
  date.toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" });

// One entry per piece and publish time, with every platform it goes out on.
const groupAdaptations = (adaptations) => {
  const byKey = new Map();
  adaptations.forEach((adaptation) => {
    if (!adaptation.scheduled_at) return;
    const key = `${adaptation.content_piece_id}-${adaptation.scheduled_at}`;
    const entry = byKey.get(key);
    if (entry) {
      if (!entry.platforms.includes(adaptation.platform)) entry.platforms.push(adaptation.platform);
      return;
    }
    byKey.set(key, {
      key,
      pieceId: adaptation.content_piece_id,
      groupId: adaptation.group_id,
      adaptationId: adaptation.id,
      configId: adaptation.config_id,
      title: adaptation.content_piece_title || "Sin título",
      clientName: adaptation.client_name,
      date: new Date(adaptation.scheduled_at),
      platforms: [adaptation.platform],
    });
  });
  return [...byKey.values()].sort((a, b) => a.date - b.date).slice(0, MAX_ITEMS);
};

function PlatformIcons({ platforms }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
      {platforms.map((platform) => {
        const meta = PLATFORM_META[platform?.toLowerCase()];
        if (!meta) return null;
        const { Icon } = meta;
        return (
          <Tooltip key={platform} title={meta.label} arrow>
            <Icon sx={{ fontSize: 15, color: meta.color }} />
          </Tooltip>
        );
      })}
    </Box>
  );
}

function UpcomingItem({ item, showClientName, clientLogo, compact, onClick }) {
  const theme = useTheme();
  const { neutrals } = useCustomTheme();

  return (
    <Box
      role="button"
      tabIndex={0}
      onClick={() => onClick?.(item)}
      onKeyDown={(event) => {
        if (event.key === "Enter") onClick?.(item);
      }}
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 0.5,
        p: 1.25,
        borderRadius: "10px",
        border: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.paper,
        cursor: "pointer",
        transition: "background-color 0.2s ease, border-color 0.2s ease",
        "&:hover": {
          backgroundColor: neutrals.gray[50],
          borderColor: theme.palette.primary.main,
        },
        ...(compact && { width: 220, flexShrink: 0 }),
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
        <Typography variant="caption" sx={{ fontWeight: 700, color: theme.palette.primary.main }}>
          {compact ? `${formatDayLabel(item.date)} · ` : ""}
          {formatTime(item.date)}
        </Typography>
        <PlatformIcons platforms={item.platforms} />
      </Box>
      <Typography
        variant="body2"
        sx={{
          fontWeight: 600,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
        title={item.title}
      >
        {item.title}
      </Typography>
      {showClientName && item.clientName && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, minWidth: 0 }}>
          <Avatar
            name={item.clientName}
            src={clientLogo || undefined}
            sx={{ width: 16, height: 16, fontSize: 8, flexShrink: 0 }}
          />
          <Typography
            variant="caption"
            sx={{ color: theme.palette.text.secondary, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
          >
            {item.clientName}
          </Typography>
        </Box>
      )}
    </Box>
  );
}

/**
 * Next scheduled publications, as a side column ("side") or a horizontal strip ("top").
 * `filters` are the adaptations-endpoint params (client/config/agency...).
 */
export function UpcomingPublications({
  layout = "side",
  filters = {},
  refreshKeys = [],
  showClientName = false,
  clientLogoByConfigId = {},
  onItemClick,
  onToggleView,
  isCalendarView = false,
  mockAdaptations = null, // tutorial: shown instead of the fetched ones
}) {
  const theme = useTheme();
  const { interceptorReady } = useAuth();

  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== null && value !== undefined && value !== "") params.append(key, value);
  });
  params.append("upcoming", "true");
  params.append("limit", String(ADAPTATIONS_LIMIT));
  const endpoint = `content-generation/adaptations/?${params.toString()}`;

  const { data: fetchedData, isLoading } = getData(
    mockAdaptations ? null : endpoint,
    interceptorReady && !mockAdaptations,
    refreshKeys
  );
  const data = mockAdaptations || fetchedData;

  const items = useMemo(() => {
    const adaptations = Array.isArray(data?.results) ? data.results : Array.isArray(data) ? data : [];
    return groupAdaptations(adaptations);
  }, [data]);

  const isSide = layout === "side";

  const header = (
    // Wraps: when the button doesn't fit next to the title it drops below it.
    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 1 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <CalendarClock size={20} color={theme.palette.primary.main} style={{ flexShrink: 0 }} />
        <Typography sx={{ fontSize: "1rem", fontWeight: 700, whiteSpace: "nowrap" }}>
          Próximas publicaciones
        </Typography>
      </Box>
      {onToggleView && (
        <Button variant="dark" size="xs" onClick={onToggleView} sx={{ whiteSpace: "nowrap" }}>
          {isCalendarView ? <List size={14} /> : <Calendar size={14} />}
          {isCalendarView ? "Ir a tabla" : "Ir a calendario"}
        </Button>
      )}
    </Box>
  );

  const emptyState = (
    <Typography variant="body2" sx={{ color: theme.palette.text.secondary, py: isSide ? 2 : 0.5, textAlign: isSide ? "center" : "left" }}>
      No hay publicaciones programadas.
    </Typography>
  );

  if (!isSide) {
    return (
      <Card data-tutorial="upcoming-publications" sx={{ flexShrink: 0, p: 1.5, gap: 1.25 }}>
        {header}
        {isLoading ? (
          <Box sx={{ display: "flex", gap: 1 }}>
            {Array.from({ length: 4 }).map((_, index) => (
              <Skeleton key={index} variant="rounded" width={220} height={72} sx={{ flexShrink: 0 }} />
            ))}
          </Box>
        ) : items.length === 0 ? (
          emptyState
        ) : (
          <Box sx={{ display: "flex", gap: 1, overflowX: "auto", pb: 0.5 }}>
            {items.map((item) => (
              <UpcomingItem
                key={item.key}
                item={item}
                compact
                showClientName={showClientName}
                clientLogo={clientLogoByConfigId[item.configId]}
                onClick={onItemClick}
              />
            ))}
          </Box>
        )}
      </Card>
    );
  }

  // Side column: items grouped under their day.
  const days = [];
  items.forEach((item) => {
    const label = formatDayLabel(item.date);
    const last = days[days.length - 1];
    if (last?.label === label) last.items.push(item);
    else days.push({ label, items: [item] });
  });

  return (
    <Card data-tutorial="upcoming-publications" sx={{ width: { lg: "clamp(260px, 24%, 320px)", xl: "clamp(320px, 26%, 440px)" }, flexShrink: 0, height: "100%", minHeight: 0, p: 1.5, gap: 1.5, overflow: "hidden" }}>
      {header}
      <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", display: "flex", flexDirection: "column", gap: 1.5, pr: 0.5 }}>
        {isLoading
          ? Array.from({ length: 5 }).map((_, index) => (
            <Skeleton key={index} variant="rounded" height={72} sx={{ flexShrink: 0 }} />
          ))
          : items.length === 0
            ? emptyState
            : days.map((day) => (
              <Box key={day.label} sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
                <Typography
                  variant="caption"
                  sx={{ fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: theme.palette.text.secondary }}
                >
                  {day.label}
                </Typography>
                {day.items.map((item) => (
                  <UpcomingItem
                    key={item.key}
                    item={item}
                    showClientName={showClientName}
                    clientLogo={clientLogoByConfigId[item.configId]}
                    onClick={onItemClick}
                  />
                ))}
              </Box>
            ))}
      </Box>
    </Card>
  );
}
